/**
 * 🎯 ZONE-BASED HOOKS
 * 
 * Clean, isolated hooks for each zone that prevent cross-zone contamination.
 * These hooks replace the existing mixed-zone hooks with pure zone implementations.
 */

import { useSelector, useDispatch } from 'react-redux';
import { useMemo } from 'react';
import {
  // Selectors
  selectCharacterZoneData,
  selectVariationZoneData,
  selectSharedDataForComponents,
  selectSelectedCharacterId,
  selectSelectedBookId,
  selectSelectedLitGroupId,
  // selectSelectedVariationId,
  selectCurrentBook,
  selectCurrentLitGroup
} from '../../selectors/zoneSelectors';

import {
  // Actions
  setLitGroupSelection,
  autoSelectFirstBook,
  autoSelectFirstCharacter,
  setBookSelection,
  setCharacterSelection,
  setVariationSelection,
  updateVersionField,
  setActiveVersionFromSamples,
  setEditMode,
  clearUnsavedChanges,
  setSubmitting,
  clearCharacterZone,
  clearVariationZone,

  // Thunks
  fetchLitGroups,
  fetchLitGroupProfile,
  fetchBooks,
  fetchCharacters,
  fetchCharactersEnhanced,
  fetchVariations,
  loadVariationForEdit,
  createCharacter,
  updateCharacter,
  deleteCharacter,
  createVariation,
  updateVariation,
  deleteVariation,
  deleteVersion
} from '../../slices/bookWorksheetDataSlice';

// ===== CHARACTER ZONE HOOK (PURE) =====
export const useCharacterZone = () => {
  const dispatch = useDispatch();
  const zoneData = useSelector(selectCharacterZoneData);

  const actions = useMemo(() => ({
    // Selection actions
    selectCharacter: (characterId) => {
      console.log('[CHARACTER_ZONE] Selecting character:', characterId);
      dispatch(setCharacterSelection(characterId));
    },

    // Data fetching actions
    fetchCharacters: (bookId) => {
      console.log('[CHARACTER_ZONE] Fetching characters for book:', bookId);
      return dispatch(fetchCharacters(bookId));
    },

    // Character CRUD actions
    createCharacter: (bookId, data) => {
      console.log('[CHARACTER_ZONE] Creating character for book:', bookId);
      return dispatch(createCharacter({ bookId, data }));
    },

    updateCharacter: (characterId, bookId, data) => {
      console.log('[CHARACTER_ZONE] Updating character:', characterId);
      return dispatch(updateCharacter({ characterId, bookId, data }));
    },

    deleteCharacter: (params) => {
      console.log('[CHARACTER_ZONE] Deleting character:', params.characterId);
      return dispatch(deleteCharacter(params));
    },

    // Zone management
    clearZone: () => {
      console.log('[CHARACTER_ZONE] Clearing zone');
      dispatch(clearCharacterZone());
    }
  }), [dispatch]);

  return useMemo(() => ({
    ...zoneData,
    actions
  }), [zoneData, actions]);
};

// ===== VARIATION ZONE HOOK (PURE) =====
export const useVariationZone = () => {
  const dispatch = useDispatch();
  const zoneData = useSelector(selectVariationZoneData);

  const actions = useMemo(() => ({
    // Selection actions
    selectVariation: (variationId) => {
      console.log('[VARIATION_ZONE] Selecting variation:', variationId);
      dispatch(setVariationSelection(variationId));
    },

    // Data fetching actions
    fetchVariations: (characterId) => {
      console.log('[VARIATION_ZONE] Fetching variations for character:', characterId);
      return dispatch(fetchVariations(characterId));
    },

    loadVariationForEdit: (variationId, characterId, litGroupId, bookId) => {
      console.log('[VARIATION_ZONE] Loading variation for edit:', variationId);
      return dispatch(loadVariationForEdit({ variationId, characterId, litGroupId, bookId }));
    },

    // Version management
    updateVersionField: (field, value) => {
      console.log('[VARIATION_ZONE] Updating version field:', field, '=', value);
      dispatch(updateVersionField({ field, value }));
    },

    setActiveVersionFromSamples: (sampleUuid) => {
      console.log('[VARIATION_ZONE] Setting active version from samples:', sampleUuid);
      dispatch(setActiveVersionFromSamples(sampleUuid));
    },

    // Edit mode management
    setEditMode: (mode) => {
      console.log('[VARIATION_ZONE] Setting edit mode:', mode);
      dispatch(setEditMode(mode));
    },

    clearUnsavedChanges: () => {
      console.log('[VARIATION_ZONE] Clearing unsaved changes');
      dispatch(clearUnsavedChanges());
    },

    setSubmitting: (isSubmitting) => {
      dispatch(setSubmitting(isSubmitting));
    },

    // Variation CRUD actions
    createVariation: (characterId, data) => {
      console.log('[VARIATION_ZONE] Creating variation for character:', characterId);
      return dispatch(createVariation({ characterId, data }));
    },

    updateVariation: (variationId, data) => {
      console.log('[VARIATION_ZONE] Updating variation:', variationId);
      return dispatch(updateVariation({ variationId, data }));
    },

    deleteVariation: (variationId) => {
      console.log('[VARIATION_ZONE] Deleting variation:', variationId);
      return dispatch(deleteVariation({ variationId }));
    },

    deleteVersion: (versionId, variationId) => {
      console.log('[VARIATION_ZONE] Deleting version:', versionId);
      return dispatch(deleteVersion({ versionId, variationId }));
    },

    // Zone management
    clearZone: () => {
      console.log('[VARIATION_ZONE] Clearing zone');
      dispatch(clearVariationZone());
    }
  }), [dispatch]);

  return useMemo(() => ({
    ...zoneData,
    actions
  }), [zoneData, actions]);
};

// ===== SHARED DATA HOOK (READ-ONLY) =====
export const useSharedData = () => {
  const dispatch = useDispatch();
  const sharedData = useSelector(selectSharedDataForComponents);

  const actions = useMemo(() => ({
    // Selection actions
    setLitGroupSelection: (litGroupId) => {
      console.log('[SHARED_DATA] Setting lit group selection:', litGroupId);
      dispatch(setLitGroupSelection(litGroupId));
    },

    setBookSelection: (bookId) => {
      console.log('[SHARED_DATA] Setting book selection:', bookId);
      dispatch(setBookSelection(bookId));
    },

    // Data fetching actions
    fetchLitGroups: () => {
      console.log('[SHARED_DATA] Fetching literary groups');
      return dispatch(fetchLitGroups());
    },

    fetchLitGroupProfile: (litGroupId) => {
      console.log('[SHARED_DATA] Fetching lit group profile:', litGroupId);
      return dispatch(fetchLitGroupProfile(litGroupId));
    },

    fetchBooks: async (litGroupId) => {
      console.log('[SHARED_DATA] Fetching books for lit group:', litGroupId);
      const result = await dispatch(fetchBooks(litGroupId)).unwrap();
      console.log('[SHARED_DATA] Fetching books for lit group: result', result);

      // Auto-select first book after fetching
      dispatch(autoSelectFirstBook());
      return result;
    },
    fetchCharacters: async (bookId) => {
      console.log('[SHARED_DATA] Fetching characters for book:', bookId);
      const result = await dispatch(fetchCharacters(bookId)).unwrap();
      // Auto-select first book after fetching
      dispatch(autoSelectFirstCharacter());
      return result;
    },

    fetchCharactersEnhanced: async (bookId) => {
      console.log('[SHARED_DATA] Fetching enhanced characters for book:', bookId);
      const result = await dispatch(fetchCharactersEnhanced(bookId)).unwrap();
      // Auto-select first character after fetching
      dispatch(autoSelectFirstCharacter());
      return result;
    }
  }), [dispatch]);

  return useMemo(() => ({
    ...sharedData,
    actions
  }), [sharedData, actions]);
};

// ===== COMPOSITE HOOKS FOR SPECIFIC USE CASES =====

// Hook for components that need character management functionality
export const useCharacterManagement = () => {
  const characterZone = useCharacterZone();
  const sharedData = useSharedData();
  const selectedBookId = useSelector(selectSelectedBookId);

  const compositeActions = useMemo(() => ({
    // Initialize character management for a book
    initializeForBook: async (bookId) => {
      console.log('[CHARACTER_MGMT] Initializing for book:', bookId);
      sharedData.actions.setBookSelection(bookId);
      return characterZone.actions.fetchCharacters(bookId);
    },

    // Select and load character
    selectAndLoadCharacter: async (characterId) => {
      console.log('[CHARACTER_MGMT] Selecting and loading character:', characterId);
      characterZone.actions.selectCharacter(characterId);
    },

    // Create new character
    createNewCharacter: async (characterData) => {
      if (!selectedBookId) {
        throw new Error('No book selected for character creation');
      }
      console.log('[CHARACTER_MGMT] Creating new character');
      return characterZone.actions.createCharacter(selectedBookId, characterData);
    }
  }), [characterZone.actions, sharedData.actions, selectedBookId]);

  return useMemo(() => ({
    // Character zone data
    characters: characterZone.characters,
    selectedCharacter: characterZone.selectedCharacter,
    loading: characterZone.loading,
    error: characterZone.error,

    // Shared data needed for character management
    litGroups: sharedData.litGroups,
    books: sharedData.books,
    selections: sharedData.selections,

    // Combined actions
    actions: {
      ...characterZone.actions,
      ...compositeActions
    }
  }), [characterZone, sharedData, compositeActions]);
};

// Hook for components that need variation management functionality
export const useVariationManagement = () => {
  const variationZone = useVariationZone();
  const selectedCharacterId = useSelector(selectSelectedCharacterId);
  const selectedLitGroupId = useSelector(selectSelectedLitGroupId);
  const selectedBookId = useSelector(selectSelectedBookId);

  const compositeActions = useMemo(() => ({
    // Initialize variation management for a character
    initializeForCharacter: async (characterId) => {
      console.log('[VARIATION_MGMT] Initializing for character:', characterId);
      return variationZone.actions.fetchVariations(characterId);
    },

    // Load variation for editing
    loadForEdit: async (variationId) => {
      if (!selectedCharacterId || !selectedLitGroupId || !selectedBookId) {
        throw new Error('Missing required selections for variation editing');
      }
      console.log('[VARIATION_MGMT] Loading variation for edit:', variationId);
      return variationZone.actions.loadVariationForEdit(
        variationId,
        selectedCharacterId,
        selectedLitGroupId,
        selectedBookId
      );
    },

    // Create new variation
    createNewVariation: async (variationData) => {
      if (!selectedCharacterId) {
        throw new Error('No character selected for variation creation');
      }
      console.log('[VARIATION_MGMT] Creating new variation');
      return variationZone.actions.createVariation(selectedCharacterId, variationData);
    },

    // Save current variation
    saveCurrentVariation: async () => {
      if (!variationZone.activeVariation) {
        throw new Error('No active variation to save');
      }
      console.log('[VARIATION_MGMT] Saving current variation');
      variationZone.actions.setSubmitting(true);
      try {
        const result = await variationZone.actions.updateVariation(
          variationZone.activeVariation.uuid,
          variationZone.activeVariation
        );
        variationZone.actions.clearUnsavedChanges();
        return result;
      } finally {
        variationZone.actions.setSubmitting(false);
      }
    }
  }), [
    variationZone.actions,
    variationZone.activeVariation,
    selectedCharacterId,
    selectedLitGroupId,
    selectedBookId
  ]);

  return useMemo(() => ({
    // Variation zone data
    variations: variationZone.variations,
    activeVariation: variationZone.activeVariation,
    activeVersion: variationZone.activeVersion,
    samples: variationZone.samples,
    variationCounts: variationZone.variationCounts,
    loading: variationZone.loading,
    error: variationZone.error,
    editMode: variationZone.editMode,
    hasUnsavedChanges: variationZone.hasUnsavedChanges,
    isSubmitting: variationZone.isSubmitting,

    // Context data
    characterId: variationZone.characterId,

    // Combined actions
    actions: {
      ...variationZone.actions,
      ...compositeActions
    }
  }), [variationZone, compositeActions]);
};

// ===== LEGACY COMPATIBILITY HOOK =====
// This hook provides backward compatibility during migration
export const useReduxSelectorsZoneBasedNew = () => {
  const characterZone = useCharacterZone();
  const variationZone = useVariationZone();
  const sharedData = useSharedData();

  // Get selected entities
  const selectedBook = useSelector(selectCurrentBook);
  const selectedLitGroup = useSelector(selectCurrentLitGroup);
  const litGroupId = useSelector(selectSelectedLitGroupId);
  const bookId = useSelector(selectSelectedBookId);
  const booksLoading = sharedData.loading?.books;
  const booksError = sharedData.errors?.books;
  const booksLastUpdated = sharedData.lastUpdated?.books;

  // Return data in the format expected by existing components
  return useMemo(() => ({
    // Character data
    characters: characterZone.characters,
    selectedCharacter: characterZone.selectedCharacter,

    // Book data - ADD MISSING FIELDS
    selectedBook,
    books: sharedData.books,
    booksLoading,
    booksError,
    booksLastUpdated,

    // Literary Group data
    selectedLitGroup,
    litGroupId,
    bookId,

    // Variation data
    variations: variationZone.variations,
    activeVariation: variationZone.activeVariation,
    activeVersion: variationZone.activeVersion,
    samples: variationZone.samples,

    // Shared data
    litGroups: sharedData.litGroups,
    selections: sharedData.selections,

    // Loading states
    loading: {
      characters: characterZone.loading,
      variations: variationZone.loading,
      litGroups: sharedData.loading.litGroups,
      books: sharedData.loading.books
    },

    // Error states
    errors: {
      characters: characterZone.error,
      variations: variationZone.error,
      litGroups: sharedData.errors.litGroups,
      books: sharedData.errors.books
    },

    // Actions (combined for compatibility)
    actions: {
      ...characterZone.actions,
      ...variationZone.actions,
      ...sharedData.actions
    }
  }), [characterZone, variationZone, sharedData, selectedBook, selectedLitGroup, litGroupId, bookId, booksLoading, booksError, booksLastUpdated]);
};
